/**
 * Geometry configuration definitions
 */

export interface GeometryConfig {
  type: string;
  shortName: string;
  label: string;  // Display name for UI
  threeClass: string;  // Name of three.js geometry class
  params: Record<string, any>;
  // List of param names in constructor order
  paramOrder: string[];
}

export const geometryConfigs: GeometryConfig[] = [
  // Basic shapes
  {
    type: 'BoxGeometry',
    shortName: 'box',
    label: 'Box',
    threeClass: 'BoxGeometry',
    params: {
      width: { value: 1, min: 0.1, max: 100, step: 0.01 },
      height: { value: 1, min: 0.1, max: 100, step: 0.01 },
      depth: { value: 1, min: 0.1, max: 100, step: 0.01 }
    },
    paramOrder: ['width', 'height', 'depth']
  },
  {
    type: 'SphereGeometry',
    shortName: 'sphere',
    label: 'Sphere',
    threeClass: 'SphereGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      widthSegments: { value: 32, min: 3, max: 64, step: 1 },
      heightSegments: { value: 16, min: 2, max: 32, step: 1 }
    },
    paramOrder: ['radius', 'widthSegments', 'heightSegments']
  },
  {
    type: 'PlaneGeometry',
    shortName: 'plane',
    label: 'Plane',
    threeClass: 'PlaneGeometry',
    params: {
      width: { value: 1, min: 0.1, max: 100, step: 0.01 },
      height: { value: 1, min: 0.1, max: 100, step: 0.01 }
    },
    paramOrder: ['width', 'height']
  },
  {
    type: 'CylinderGeometry',
    shortName: 'cylinder',
    label: 'Cylinder',
    threeClass: 'CylinderGeometry',
    params: {
      radiusTop: { value: 1, min: 0, max: 100, step: 0.01 },
      radiusBottom: { value: 1, min: 0, max: 100, step: 0.01 },
      height: { value: 1, min: 0.1, max: 100, step: 0.01 },
      radialSegments: { value: 32, min: 3, max: 64, step: 1 }
    },
    paramOrder: ['radiusTop', 'radiusBottom', 'height', 'radialSegments']
  },
  {
    type: 'ConeGeometry',
    shortName: 'cone',
    label: 'Cone',
    threeClass: 'ConeGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      height: { value: 1, min: 0.1, max: 100, step: 0.01 },
      radialSegments: { value: 32, min: 3, max: 64, step: 1 }
    },
    paramOrder: ['radius', 'height', 'radialSegments']
  },
  {
    type: 'TorusGeometry',
    shortName: 'torus',
    label: 'Torus',
    threeClass: 'TorusGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      tube: { value: 0.4, min: 0.01, max: 100, step: 0.01 },
      radialSegments: { value: 16, min: 3, max: 64, step: 1 },
      tubularSegments: { value: 100, min: 3, max: 200, step: 1 }
    },
    paramOrder: ['radius', 'tube', 'radialSegments', 'tubularSegments']
  },
  {
    type: 'TorusKnotGeometry',
    shortName: 'knot',
    label: 'Torus Knot',
    threeClass: 'TorusKnotGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      tube: { value: 0.4, min: 0.01, max: 100, step: 0.01 },
      tubularSegments: { value: 64, min: 3, max: 300, step: 1 },
      radialSegments: { value: 8, min: 3, max: 64, step: 1 },
      p: { value: 2, min: 1, max: 20, step: 1 },
      q: { value: 3, min: 1, max: 20, step: 1 }
    },
    paramOrder: ['radius', 'tube', 'tubularSegments', 'radialSegments', 'p', 'q']
  },
  {
    type: 'CapsuleGeometry',
    shortName: 'capsule',
    label: 'Capsule',
    threeClass: 'CapsuleGeometry',
    params: {
      radius: { value: 0.5, min: 0.01, max: 100, step: 0.01 },
      length: { value: 1, min: 0.01, max: 100, step: 0.01 },
      capSegments: { value: 10, min: 1, max: 32, step: 1 },
      radialSegments: { value: 20, min: 3, max: 64, step: 1 }
    },
    paramOrder: ['radius', 'length', 'capSegments', 'radialSegments']
  },
  {
    type: 'CircleGeometry',
    shortName: 'circle',
    label: 'Circle',
    threeClass: 'CircleGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      segments: { value: 32, min: 3, max: 128, step: 1 }
    },
    paramOrder: ['radius', 'segments']
  },
  {
    type: 'RingGeometry',
    shortName: 'ring',
    label: 'Ring',
    threeClass: 'RingGeometry',
    params: {
      innerRadius: { value: 0.5, min: 0, max: 100, step: 0.01 },
      outerRadius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      thetaSegments: { value: 32, min: 3, max: 128, step: 1 }
    },
    paramOrder: ['innerRadius', 'outerRadius', 'thetaSegments']
  },

  // Platonic solids
  {
    type: 'TetrahedronGeometry',
    shortName: 'tetra',
    label: 'Tetrahedron',
    threeClass: 'TetrahedronGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      detail: { value: 0, min: 0, max: 5, step: 1 }
    },
    paramOrder: ['radius', 'detail']
  },
  {
    type: 'OctahedronGeometry',
    shortName: 'octa',
    label: 'Octahedron',
    threeClass: 'OctahedronGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      detail: { value: 0, min: 0, max: 5, step: 1 }
    },
    paramOrder: ['radius', 'detail']
  },
  {
    type: 'DodecahedronGeometry',
    shortName: 'dodeca',
    label: 'Dodecahedron',
    threeClass: 'DodecahedronGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      detail: { value: 0, min: 0, max: 5, step: 1 }
    },
    paramOrder: ['radius', 'detail']
  },
  {
    type: 'IcosahedronGeometry',
    shortName: 'icosa',
    label: 'Icosahedron',
    threeClass: 'IcosahedronGeometry',
    params: {
      radius: { value: 1, min: 0.1, max: 100, step: 0.01 },
      detail: { value: 0, min: 0, max: 5, step: 1 }
    },
    paramOrder: ['radius', 'detail']
  }
];
